import { WebAudioEngine } from "@readium/navigator";
import {
  type Clip,
  GuidedNavigationDocument,
  type GuidedNavigationObject,
  type Publication,
} from "@readium/shared";
import { Direction } from "./GuidedNavigationService";
import { assertNever } from "../../../modules/shared-utils/assertions";

export enum UpdateTimeResult {
  NoClips = 0,
  OnClip = 1,
  BetweenClips = 2,
  Beginning = 3,
  End = 4,
}

export default class GuidedNavigationManager {
  private cachedClips: Clip[];
  private _activeElement: Clip | null;
  private _hasPrevious: boolean = false;
  private _hasNext: boolean = false;

  public get hasPrevious(): boolean {
    return this._hasPrevious;
  }

  public get hasNext(): boolean {
    return this._hasNext;
  }

  constructor(
    private readonly publication: Publication,
    private readonly audioPlayer: WebAudioEngine,
    public readonly href: string,
    private readonly guidedDocument: GuidedNavigationDocument
  ) {}

  public activate(
    index: number,
    resolve: () => void,
    reject: (reason?) => void
  ): GuidedNavigationManager {
    const guided = this.guided[index];

    const onReady = () => {
      resolve();
      cleanupHandlers();
    };
    const onError = (ev) => {
      reject(ev);
      cleanupHandlers();
    };

    const setupHandlers = () => {
      mediaElement.addEventListener("canplay", onReady, { once: true });
      mediaElement.addEventListener("error", onError, { once: true });
    };
    const cleanupHandlers = () => {
      mediaElement.removeEventListener("canplay", onReady);
      mediaElement.removeEventListener("error", onError);
    };

    // @ts-expect-error we need the private element to handle loading
    const mediaElement = this.audioPlayer.mediaElement as HTMLMediaElement;

    if (mediaElement.src?.endsWith(guided.audioFile)) {
      if (mediaElement.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        resolve();
      } else {
        setupHandlers();
      }
    } else {
      setupHandlers();
      this.load(guided);
    }
    return this;
  }

  public async start(index: number): Promise<boolean> {
    this.audioPlayer.stop();

    const clip = this.clips?.[index];
    if (!clip) return false;

    this.audioPlayer.skip(clip.start);
    await this.audioPlayer.play();

    return true;
  }

  public async go(direction: Direction): Promise<boolean> {
    const currentTime = this.audioPlayer.currentTime();
    const positions = this.at(currentTime);

    const destination =
      direction === Direction.Previous
        ? positions.previous
        : direction === Direction.Next
          ? positions.next
          : assertNever(direction);

    if (!destination) return false;

    const diff = destination.start - currentTime;

    this.audioPlayer.skip(diff);
    if (!this.audioPlayer.isPlaying()) {
      await this.audioPlayer.play();
    }
    return true;
  }

  public get activeId(): string | null {
    return this._activeElement?.fragmentId ?? null;
  }

  public updateTime(currentTime: number): UpdateTimeResult {
    if (!this.clips.length) return UpdateTimeResult.NoClips;

    // on the same clip previously reached
    if (
      this._activeElement &&
      this._activeElement.start <= currentTime &&
      this._activeElement.end >= currentTime
    ) {
      return UpdateTimeResult.OnClip;
    }

    const { previous, current, next } = this.at(currentTime);
    this._hasPrevious = !!previous;
    this._hasNext = !!next;

    if (current) {
      this._activeElement = current;
      return UpdateTimeResult.OnClip;
    } else if (!previous) {
      return UpdateTimeResult.Beginning;
    } else if (next) {
      return UpdateTimeResult.BetweenClips;
    }

    this._activeElement = null;
    return UpdateTimeResult.End;
  }

  private at(time: number): { previous: Clip; current: Clip; next: Clip } {
    let previous: Clip | null = null;
    let next: Clip | null = null;
    let current: Clip | null = null;

    for (const clip of this.clips) {
      if (clip.start <= time && clip.end >= time) {
        current = clip;
      } else if (clip.start > time && (!next || clip.start < next.start)) {
        next = clip;
      } else if (clip.end < time && (!previous || clip.end > previous.end)) {
        previous = clip;
      }
    }

    return { previous, current, next };
  }

  public get guided(): GuidedNavigationObject[] | undefined {
    if (!this.guidedDocument.guided?.length) return undefined;
    const mapToChild = (g: GuidedNavigationObject) =>
      g.children?.length ? g.children.map((c) => mapToChild(c)) : g;
    return this.guidedDocument.guided.map((g) => mapToChild(g)).flat();
  }

  public get empty(): boolean {
    return !this.guided?.length;
  }

  public get clips(): Clip[] {
    if (!this.cachedClips) this.cachedClips = this.guided.map((g) => g.clip);
    return this.cachedClips;
  }

  public getClipIndex(clipID: string): number {
    if (!this._activeElement) return -1;
    return this.clips.findIndex((clip) => clip.fragmentId === clipID);
  }

  // TODO: READER-393
  public resetPlayer(duration?: number, volume?: number): void {
    if ("mediaSession" in navigator) {
      const covers = this.publication.manifest.linksWithRel("cover");
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.publication.metadata.title.getTranslation(),
        artwork: covers.length
          ? [
              {
                src: covers[0].toURL(this.publication.baseURL),
                type: covers[0].mediaType.string,
              },
            ]
          : [],
        artist:
          this.publication.metadata?.authors?.items
            .map((a) => a.name.getTranslation())
            .join(", ") || undefined,
        album:
          this.publication.metadata.publishers?.items
            .map((p) => p.name.getTranslation())
            .join(", ") || undefined,
      });
      navigator.mediaSession.setActionHandler("play", () =>
        this.audioPlayer.play()
      );
      navigator.mediaSession.setActionHandler("pause", () =>
        this.audioPlayer.pause()
      );
      navigator.mediaSession.setActionHandler("stop", () =>
        this.audioPlayer.stop()
      );
      navigator.mediaSession.setActionHandler("seekbackward", () =>
        this.audioPlayer.skip(-10)
      );
      navigator.mediaSession.setActionHandler("seekforward", () =>
        this.audioPlayer.skip(10)
      );
      navigator.mediaSession.setActionHandler("seekto", (details) =>
        this.audioPlayer.skip(details.seekTime)
      );
      // TODO handler for next/prev track
    }
  }

  // TODO: READER-393
  public stop() {
    this.audioPlayer.stop();
    this._activeElement = null;
    if ("mediaSession" in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata();
      navigator.mediaSession.setActionHandler("play", null);
      navigator.mediaSession.setActionHandler("pause", null);
      navigator.mediaSession.setActionHandler("stop", null);
      navigator.mediaSession.setActionHandler("seekbackward", null);
      navigator.mediaSession.setActionHandler("seekforward", null);
      navigator.mediaSession.setActionHandler("seekto", null);
    }
  }

  // Load an audio [Link] in the player
  private load(object: GuidedNavigationObject) {
    const l = this.publication.linkWithHref(object.audioFile);
    if (!l)
      throw new Error(
        `No link found in publication for audio file ${object.audioFile}`
      );
    this.audioPlayer.loadAudio(l.toURL(this.publication.baseURL));
  }
}
